// CitySpecLanding.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { db } from "./firebase";
import LandingPagePracto from "./LandingPage";

// --- helpers ---
const toTitle = (s) =>
  decodeURIComponent(s || "")
    .replace(/-/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/(^|\s)\S/g, (m) => m.toUpperCase());

const slugify = (s) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/ë/g, "e")
    .replace(/ç/g, "c")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

async function fetchSpecialties() {
  const snap = await getDocs(
    query(collection(db, "specialties"), orderBy("updatedAt", "desc"), limit(500))
  );
  return snap.docs.map((d) => ({ key: d.id, ...d.data() })); // key = doc id
}

export default function CitySpecLanding() {
  const { lang, citySlug, specSlug } = useParams(); // route: /:lang/:citySlug/:specSlug
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const langClean = (lang || "sq").toLowerCase() === "en" ? "en" : "sq";
  const city = useMemo(() => toTitle(citySlug), [citySlug]);

  const [resolvedSpecKey, setResolvedSpecKey] = useState(null); // null = not resolved yet
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const list = await fetchSpecialties();
        const wantedSlug = slugify(specSlug);

        // match ONLY using the URL language first
        let found =
          list.find((x) => slugify(x?.labels?.[langClean]) === wantedSlug) ||
          // fallback: try other label language
          list.find((x) => slugify(x?.labels?.sq) === wantedSlug) ||
          list.find((x) => slugify(x?.labels?.en) === wantedSlug) ||
          // fallback: doc id itself
          list.find((x) => slugify(x?.key) === wantedSlug) ||
          // fallback: synonyms (optional)
          list.find((x) => (Array.isArray(x?.synonymsEn) ? x.synonymsEn : []).some((v) => slugify(v) === wantedSlug)) ||
          list.find((x) => (Array.isArray(x?.synonymsSq) ? x.synonymsSq : []).some((v) => slugify(v) === wantedSlug));

        if (!alive) return;
        setResolvedSpecKey(found?.key || ""); // "" means "no specialty"
      } catch {
        if (!alive) return;
        setResolvedSpecKey("");
      } finally {
        if (alive) setReady(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, [specSlug, langClean]);

  useEffect(() => {
    // ✅ DO NOT redirect until resolver is done
    if (!ready) return;

    const params = new URLSearchParams(sp);
    params.set("city", city);
    params.set("lang", langClean);

    // ✅ spec MUST be the stable key (specialties doc id)
    if (resolvedSpecKey) params.set("spec", resolvedSpecKey);
    else params.delete("spec");

    navigate(`/?${params.toString()}`, { replace: true });
  }, [ready, resolvedSpecKey, city, langClean, sp, navigate]);

  return <LandingPagePracto />;
}
